import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Button, Modal, Form, Input, Select, DatePicker, Tag, Typography, Space, Tabs, AutoComplete, App } from 'antd';
import { MailOutlined, SendOutlined, EyeOutlined, DeleteOutlined, PlusOutlined, UserOutlined, BarChartOutlined, GiftOutlined, EditOutlined, CopyOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const { Title: AntTitle, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

// Styled Components
const StyledCard = styled(Card)`
  .ant-card-head-title {
    font-weight: 600;
    color: #1a1f2e;
  }

  .ant-statistic-title {
    color: #64748b;
    font-size: 14px;
  }

  .ant-statistic-content {
    color: #1a1f2e;
    font-weight: 700;
  }
`;

const ActionButton = styled(Button)`
  border-radius: 8px;
  font-weight: 600;
`;

// Types
interface EmailStats {
  period: {
    days: number;
    startDate: string;
  };
  emails: {
    sent: number;
    opened: number;
    clicked: number;
    bounced: number;
    openRate: string;
    clickRate: string;
    bounceRate: string;
  };
  subscribers: {
    total: number;
    marketingOptIn: number;
    bounced: number;
  };
  recentCampaigns: any[];
}

interface EmailCampaign {
  _id: string;
  name: string;
  subject: string;
  template: string;
  segment: string;
  status: string;
  createdAt: string;
  scheduledAt?: string;
  sentAt?: string;
  content?: {
    headline?: string;
    subheadline?: string;
    bodyContent?: string;
    ctaText?: string;
    ctaUrl?: string;
    promoCode?: string;
    promoDiscount?: string;
  };
  stats: {
    total: number;
    sent: number;
    opened: number;
    clicked: number;
  };
}

interface Voucher {
  _id: string;
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
  maxDiscount?: number;
  quota?: number;
  usedCount?: number;
  validUntil?: string;
  isActive: boolean;
}

const EmailPage: React.FC = () => {
  const { message, modal } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<EmailCampaign | null>(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchEmailStats();
    } else if (activeTab === 'campaigns') {
      fetchCampaigns();
    }
  }, [activeTab]);

  const fetchEmailStats = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/email/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        message.error('Gagal memuat statistik email');
      }
    } catch (error) {
      console.error('Fetch stats error:', error);
      message.error('Terjadi kesalahan saat memuat data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/email/campaigns');
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns);
      } else {
        message.error('Gagal memuat campaign');
      }
    } catch (error) {
      console.error('Fetch campaigns error:', error);
      message.error('Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  };

  const fetchVouchers = async () => {
    try {
      const response = await fetch('/api/admin/vouchers');
      if (response.ok) {
        const data = await response.json();
        setVouchers(data.vouchers.filter((v: Voucher) => v.isActive));
      }
    } catch (error) {
      console.error('Fetch vouchers error:', error);
    }
  };

  // Voucher options for AutoComplete
  const voucherOptions = vouchers.map(v => {
    const discountText = v.type === 'percentage' 
      ? `${v.value}%${v.maxDiscount ? ` (max ${v.maxDiscount.toLocaleString('id-ID')})` : ''}`
      : `Rp ${v.value.toLocaleString('id-ID')}`;
    const quotaText = v.quota ? ` • ${v.usedCount || 0}/${v.quota} used` : '';
    const validText = v.validUntil ? ` • s/d ${new Date(v.validUntil).toLocaleDateString('id-ID')}` : '';
    
    return {
      value: v.code,
      label: (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            <GiftOutlined style={{ marginRight: 8, color: '#2DBCAF' }} />
            <strong>{v.code}</strong>
          </span>
          <span style={{ color: '#64748b', fontSize: '12px' }}>
            {discountText}{quotaText}{validText}
          </span>
        </div>
      ),
    };
  });

  const handleVoucherSelect = (code: string) => {
    const voucher = vouchers.find(v => v.code === code);
    if (voucher) {
      const discountText = voucher.type === 'percentage' 
        ? `${voucher.value}% OFF`
        : `Rp ${voucher.value.toLocaleString('id-ID')} OFF`;
      form.setFieldsValue({ promoDiscount: discountText });
    }
  };

  const saveCampaign = async (values: any) => {
    try {
      const isEditing = !!editingCampaign;
      const url = isEditing 
        ? `/api/email/campaigns/${editingCampaign._id}`
        : '/api/email/campaigns';
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name,
          subject: values.subject,
          template: values.template,
          content: {
            headline: values.headline,
            subheadline: values.subheadline,
            bodyContent: values.bodyContent,
            ctaText: values.ctaText,
            ctaUrl: values.ctaUrl,
            promoCode: values.promoCode,
            promoDiscount: values.promoDiscount,
          },
          segment: values.segment,
          scheduledAt: values.scheduledAt?.toISOString() || null,
        }),
      });

      if (response.ok) {
        message.success(isEditing ? 'Campaign berhasil diupdate' : 'Campaign berhasil dibuat');
        handleCloseModal();
        fetchCampaigns();
      } else {
        const data = await response.json();
        message.error(data.error || (isEditing ? 'Gagal update campaign' : 'Gagal membuat campaign'));
      }
    } catch (error) {
      console.error('Save campaign error:', error);
      message.error('Terjadi kesalahan');
    }
  };

  const handleEditCampaign = (campaign: EmailCampaign) => {
    setEditingCampaign(campaign);
    fetchVouchers();
    
    // Prefill form with campaign data
    const content = campaign.content as any || {};
    form.setFieldsValue({
      name: campaign.name,
      subject: campaign.subject,
      template: campaign.template,
      headline: content.headline,
      subheadline: content.subheadline,
      bodyContent: content.bodyContent,
      ctaText: content.ctaText,
      ctaUrl: content.ctaUrl,
      promoCode: content.promoCode,
      promoDiscount: content.promoDiscount,
      segment: campaign.segment,
      scheduledAt: campaign.scheduledAt ? dayjs(campaign.scheduledAt) : undefined,
    });
    
    setShowCampaignModal(true);
  };

  const handleCloseModal = () => {
    setShowCampaignModal(false);
    setEditingCampaign(null);
    form.resetFields();
  };

  const handleDuplicateCampaign = (campaign: EmailCampaign) => {
    setEditingCampaign(null);
    fetchVouchers();
    
    // Prefill form with campaign data but as new campaign
    const content = campaign.content as any || {};
    form.setFieldsValue({
      name: `${campaign.name} (Copy)`,
      subject: campaign.subject,
      template: campaign.template,
      headline: content.headline,
      subheadline: content.subheadline,
      bodyContent: content.bodyContent,
      ctaText: content.ctaText,
      ctaUrl: content.ctaUrl,
      promoCode: content.promoCode,
      promoDiscount: content.promoDiscount,
      segment: campaign.segment,
      scheduledAt: undefined, // Reset schedule
    });
    
    setShowCampaignModal(true);
    message.info('Campaign di-duplicate. Edit dan simpan sebagai campaign baru.');
  };

  const handlePreviewCampaign = async (campaign: EmailCampaign) => {
    try {
      const response = await fetch(`/api/email/campaigns/${campaign._id}/preview`);
      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html);
        setShowPreviewModal(true);
      } else {
        message.error('Gagal memuat preview');
      }
    } catch (error) {
      console.error('Preview error:', error);
      message.error('Terjadi kesalahan');
    }
  };

  const sendCampaign = async (campaignId: string) => {
    modal.confirm({
      title: 'Kirim Campaign',
      content: 'Apakah Anda yakin ingin mengirim campaign ini?',
      okText: 'Ya, Kirim',
      cancelText: 'Batal',
      onOk: async () => {
        try {
          const response = await fetch(`/api/email/campaigns/${campaignId}/send`, {
            method: 'POST',
          });

          if (response.ok) {
            message.success('Campaign sedang dikirim');
            fetchCampaigns();
          } else {
            const data = await response.json();
            message.error(data.error || 'Gagal mengirim campaign');
          }
        } catch (error) {
          console.error('Send campaign error:', error);
          message.error('Terjadi kesalahan');
        }
      },
    });
  };

  const deleteCampaign = async (campaignId: string) => {
    modal.confirm({
      title: 'Hapus Campaign',
      content: 'Apakah Anda yakin ingin menghapus campaign ini?',
      okText: 'Ya, Hapus',
      cancelText: 'Batal',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await fetch(`/api/email/campaigns/${campaignId}`, {
            method: 'DELETE',
          });

          if (response.ok) {
            message.success('Campaign berhasil dihapus');
            fetchCampaigns();
          } else {
            const data = await response.json();
            message.error(data.error || 'Gagal menghapus campaign');
          }
        } catch (error) {
          console.error('Delete campaign error:', error);
          message.error('Terjadi kesalahan');
        }
      },
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent': return 'green';
      case 'sending': return 'blue';
      case 'scheduled': return 'orange';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'sent': return 'Terkirim';
      case 'sending': return 'Mengirim';
      case 'scheduled': return 'Terjadwal';
      case 'draft': return 'Draft';
      default: return status;
    }
  };

  const campaignColumns = [
    {
      title: 'Nama Campaign',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: EmailCampaign) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1a1f2e' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#64748b' }}>{record.subject}</div>
        </div>
      ),
    },
    {
      title: 'Template',
      dataIndex: 'template',
      key: 'template',
      render: (template: string) => (
        <Tag color="blue">{template}</Tag>
      ),
    },
    {
      title: 'Segment',
      dataIndex: 'segment',
      key: 'segment',
      render: (segment: string) => (
        <Tag color="purple">{segment}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: 'Recipients',
      key: 'recipients',
      render: (record: EmailCampaign) => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 600 }}>{record.stats.total}</div>
          {record.stats.sent > 0 && (
            <div style={{ fontSize: '12px', color: '#64748b' }}>
              {record.stats.sent} terkirim
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('id-ID'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 320,
      render: (record: EmailCampaign) => (
        <Space wrap>
          <Button 
            size="small" 
            icon={<EyeOutlined />}
            onClick={() => handlePreviewCampaign(record)}
          >
            Preview
          </Button>
          <Button 
            size="small" 
            icon={<CopyOutlined />}
            onClick={() => handleDuplicateCampaign(record)}
          >
            Duplicate
          </Button>
          {(record.status === 'draft' || record.status === 'scheduled') && (
            <>
              <Button 
                size="small" 
                icon={<EditOutlined />}
                onClick={() => handleEditCampaign(record)}
              >
                Edit
              </Button>
              <Button 
                type="primary" 
                size="small" 
                icon={<SendOutlined />}
                onClick={() => sendCampaign(record._id)}
              >
                Send
              </Button>
            </>
          )}
          <Button 
            danger 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => deleteCampaign(record._id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  // Chart data for email stats
  const chartData = stats ? {
    labels: ['Sent', 'Opened', 'Clicked'],
    datasets: [{
      label: 'Email Performance',
      data: [stats.emails.sent, stats.emails.opened, stats.emails.clicked],
      borderColor: '#2DBCAF',
      backgroundColor: 'rgba(45, 188, 175, 0.1)',
      borderWidth: 2,
      fill: true,
    }],
  } : { labels: [], datasets: [] };

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <AntTitle level={2} style={{ margin: 0, color: '#1a1f2e' }}>
          📧 Email Marketing
        </AntTitle>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="📊 Dashboard" key="dashboard">
          {stats && (
            <>
              {/* Stats Cards */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                  <StyledCard>
                    <Statistic 
                      title="Total Subscribers"
                      value={stats.subscribers.total}
                      prefix={<UserOutlined />}
                      valueStyle={{ color: '#2DBCAF' }}
                    />
                  </StyledCard>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <StyledCard>
                    <Statistic 
                      title="Marketing Opt-in"
                      value={stats.subscribers.marketingOptIn}
                      prefix={<MailOutlined />}
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </StyledCard>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <StyledCard>
                    <Statistic 
                      title="Emails Sent (30d)"
                      value={stats.emails.sent}
                      prefix={<SendOutlined />}
                      valueStyle={{ color: '#1890ff' }}
                    />
                  </StyledCard>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <StyledCard>
                    <Statistic 
                      title="Open Rate"
                      value={`${stats.emails.openRate}%`}
                      prefix={<EyeOutlined />}
                      valueStyle={{ color: '#722ed1' }}
                    />
                  </StyledCard>
                </Col>
              </Row>

              {/* Performance Metrics */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} lg={12}>
                  <StyledCard title="📈 Email Performance (Last 30 Days)">
                    <Row gutter={16}>
                      <Col span={8}>
                        <Statistic 
                          title="Open Rate"
                          value={`${stats.emails.openRate}%`}
                          valueStyle={{ fontSize: '20px', color: '#52c41a' }}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic 
                          title="Click Rate"
                          value={`${stats.emails.clickRate}%`}
                          valueStyle={{ fontSize: '20px', color: '#1890ff' }}
                        />
                      </Col>
                      <Col span={8}>
                        <Statistic 
                          title="Bounce Rate"
                          value={`${stats.emails.bounceRate}%`}
                          valueStyle={{ fontSize: '20px', color: '#ff4d4f' }}
                        />
                      </Col>
                    </Row>
                  </StyledCard>
                </Col>
                <Col xs={24} lg={12}>
                  <StyledCard title="📊 Email Volume">
                    <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {chartData.datasets.length > 0 && (
                        <Line 
                          data={chartData}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                              y: { beginAtZero: true }
                            }
                          }}
                        />
                      )}
                    </div>
                  </StyledCard>
                </Col>
              </Row>

              {/* Recent Campaigns */}
              <StyledCard title="🚀 Recent Campaigns">
                {stats.recentCampaigns.length > 0 ? (
                  <div>
                    {stats.recentCampaigns.map((campaign, index) => (
                      <div key={index} style={{ 
                        padding: '12px', 
                        borderBottom: index < stats.recentCampaigns.length - 1 ? '1px solid #f0f0f0' : 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ fontWeight: 600, color: '#1a1f2e' }}>{campaign.name}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {new Date(campaign.createdAt).toLocaleDateString('id-ID')}
                          </div>
                        </div>
                        <Tag color={getStatusColor(campaign.status)}>
                          {getStatusText(campaign.status)}
                        </Tag>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    Belum ada campaign
                  </div>
                )}
              </StyledCard>
            </>
          )}
        </TabPane>

        <TabPane tab="📮 Campaigns" key="campaigns">
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            <AntTitle level={4} style={{ margin: 0 }}>Manage Campaigns</AntTitle>
            <ActionButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingCampaign(null);
                form.resetFields();
                fetchVouchers();
                setShowCampaignModal(true);
              }}
            >
              New Campaign
            </ActionButton>
          </div>

          <Table 
            dataSource={campaigns}
            columns={campaignColumns}
            loading={loading}
            rowKey="_id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
            }}
          />
        </TabPane>
      </Tabs>

      {/* Create Campaign Modal */}
      <Modal
        title={editingCampaign ? '✏️ Edit Campaign' : '🚀 Create New Campaign'}
        open={showCampaignModal}
        onCancel={handleCloseModal}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={saveCampaign}
          style={{ marginTop: 20 }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Campaign Name"
                rules={[{ required: true, message: 'Please enter campaign name' }]}
              >
                <Input placeholder="e.g., Black Friday Promo" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="template"
                label="Template"
                rules={[{ required: true, message: 'Please select template' }]}
              >
                <Select placeholder="Select template">
                  <Select.Option value="promo">Promo Campaign</Select.Option>
                  <Select.Option value="digest">Weekly Digest</Select.Option>
                  <Select.Option value="winback">Win-back</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="subject"
            label="Subject Line"
            rules={[{ required: true, message: 'Please enter subject' }]}
          >
            <Input placeholder="Your amazing subject line" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="headline"
                label="Headline"
                rules={[{ required: true, message: 'Please enter headline' }]}
              >
                <Input placeholder="Main headline" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="subheadline" label="Subheadline">
                <Input placeholder="Supporting text (optional)" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="bodyContent"
            label="Body Content"
            rules={[{ required: true, message: 'Please enter body content' }]}
          >
            <TextArea rows={4} placeholder="Email body content..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="ctaText"
                label="CTA Button Text"
                rules={[{ required: true, message: 'Please enter CTA text' }]}
              >
                <Input placeholder="e.g., Shop Now" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="ctaUrl"
                label="CTA URL"
                rules={[{ required: true, message: 'Please enter CTA URL' }]}
              >
                <Input placeholder="https://kindergame.id/premium" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="promoCode" label="Promo Code (Optional)">
                <AutoComplete
                  options={voucherOptions}
                  placeholder="Pilih atau ketik kode"
                  onSelect={handleVoucherSelect}
                  filterOption={(inputValue, option) =>
                    option?.value.toUpperCase().includes(inputValue.toUpperCase()) ?? false
                  }
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="promoDiscount" label="Discount Text">
                <Input placeholder="e.g., 20% OFF (auto-fill dari voucher)" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="segment"
                label="Target Audience"
                rules={[{ required: true, message: 'Please select segment' }]}
              >
                <Select placeholder="Select audience">
                  <Select.Option value="all">📧 All Users (opted-in)</Select.Option>
                  <Select.Option value="all_force">🔔 All Users (ignore preferences)</Select.Option>
                  <Select.Option value="free">🆓 Free Users</Select.Option>
                  <Select.Option value="premium">⭐ Premium Users</Select.Option>
                  <Select.Option value="inactive">😴 Inactive Users (30+ days)</Select.Option>
                  <Select.Option value="new">🆕 New Users (last 7 days)</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="scheduledAt" label="Schedule (Optional)">
            <DatePicker showTime placeholder="Send immediately if not set" style={{ width: '100%' }} />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Preview Modal */}
      <Modal
        title="📧 Email Preview"
        open={showPreviewModal}
        onCancel={() => setShowPreviewModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowPreviewModal(false)}>
            Tutup
          </Button>
        ]}
        width={700}
        styles={{ body: { padding: 0, maxHeight: '70vh', overflow: 'auto' } }}
      >
        <div 
          dangerouslySetInnerHTML={{ __html: previewHtml }}
          style={{ background: '#f5f5f5' }}
        />
      </Modal>
    </div>
  );
};

export default EmailPage;